import React, { useState, useMemo, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { DownloadIcon } from "@radix-ui/react-icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { ExcelExporter } from "../../../Lib/ExcelExporter";
import { Toaster, toast } from "sonner";
import { MoreHorizontal } from "lucide-react";
import CrearEditarEstudiante from "./CrearEstudiante";
import { getStudents } from "../../../provider/adm/EstudiantePersonalizado/getStudents";
import { delateStudentAPI } from "../../../provider/adm/EstudiantePersonalizado/delateStudent";
import Loader from "../../Loader/Loader";
import { changeStatusStudent } from "../../../provider/adm/EstudiantePersonalizado/changeStatus";
import { DataTableDemoTemplate } from "../../ui/DataTableAdjusted";
import ScrollListProfesores from "../Grupos/ScrollListProfesores";

export function DataTableDemo({ status }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [profesorSelectedToFilter, setprofesorSelectedToFilter] = useState([]);
  const [ciudadFilter, setCiudadFilter] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Función para comparar nombres (igual que en ScrollListProfesores)
  const compareNames = (name1, name2) => {
    if (!name1 || !name2) return false;
    const normalize = (str) => str.trim().replace(/\s+/g, " ").toLowerCase();
    return normalize(name1) === normalize(name2);
  };

  //TraerData
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const data_fromAPI = await getStudents(status);
        setData(data_fromAPI);
      } catch (error) {
        console.error("Error fetching students:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [status, setLoading]);

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, ciudadFilter, profesorSelectedToFilter]);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        ["fullName", "email", "id"].some((key) => {
          const value = item[key];
          return (
            typeof value === "string" &&
            value.toLowerCase().includes(searchTerm.toLowerCase())
          );
        })
      );
    }

    if (ciudadFilter && ciudadFilter.length > 0) {
      filtered = filtered.filter((item) => ciudadFilter.includes(item.office));
    }

    // Filtro por profesor mejorado con compareNames
    if (profesorSelectedToFilter && profesorSelectedToFilter.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.teacherDescription || !item.teacherDescription.fullName) {
          return false;
        }
        return profesorSelectedToFilter.some((selectedName) =>
          compareNames(selectedName, item.teacherDescription.fullName)
        );
      });
    }

    return filtered;
  }, [data, searchTerm, ciudadFilter, profesorSelectedToFilter]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  //Tabla config
  const columns = [
    {
      accessorKey: "ID",
      header: "ID",
      cell: ({ row }) => <div>{row.getValue("ID")}</div>,
    },
    {
      accessorKey: "photo",
      header: "Photo",
      cell: ({ row }) => (
        <img
          src={row.original.photo || "/profilephoto.jpeg"}
          alt="User"
          className="h-10 w-10 object-cover rounded-full"
        />
      ),
    },
    {
      accessorKey: "fullName",
      header: "Name",
      cell: ({ row }) => <div>{row.getValue("fullName")}</div>,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <div>{row.getValue("email")}</div>,
    },
    {
      accessorKey: "latestPurchasedHour.hours",
      header: "Horas Compradas",
      cell: ({ row }) => {
        const latestPurchasedHour = row.original.latestPurchasedHour;
        const hours = latestPurchasedHour ? latestPurchasedHour.hours : null;
        return <div className="text-center">{hours !== null ? hours : ""}</div>;
      },
    },
    {
      accessorKey: "hoursRemaining",
      header: "Horas Restantes",
      cell: ({ row }) => (
        <div className="text-center">{row.getValue("hoursRemaining")}</div>
      ),
    },
    {
      accessorKey: "office",
      header: "Ciudad a la que pertenece",
      cell: ({ row }) => (
        <div className="text-center">{row.getValue("office")}</div>
      ),
    },
    {
      accessorKey: "teacherDescription",
      header: "Nombre del profesor",
      cell: ({ row }) => (
        <div className="text-center">
          {row.getValue("teacherDescription")?.fullName || "No asignado"}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div
          className={`flex items-center justify-center p-1 rounded-lg text-white font-semibold ${
            row.original.status ? "bg-green-500" : "bg-red-500"
          }`}
        >
          <span className="w-2 h-2 rounded-full mr-3 bg-white"></span>
          <span>{row.original.status ? "Activo" : "Inactivo"}</span>
        </div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const student = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem>
                <Sheet>
                  <SheetTrigger>
                    <Button variant="ghost">Editar</Button>
                  </SheetTrigger>
                  <SheetContent
                    side="right"
                    className="w-full md:w-1/3 bg-gray-100 text-black min-h-screen p-6 overflow-y-auto"
                  >
                    <SheetHeader>
                      <SheetTitle className="text-xl font-bold mb-4">
                        Editar estudiante individual
                      </SheetTitle>
                      <SheetDescription>
                        Llena los datos para editar un estudiante{" "}
                        {student.fullName}.
                      </SheetDescription>
                    </SheetHeader>
                    <CrearEditarEstudiante data={student} context={"edit"} />
                  </SheetContent>
                </Sheet>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async (value) => {
                  setLoading(true);
                  try {
                    await changeStatusStudent(student.ID);
                    toast.success("Estado del estudiante cambiado con éxito");
                    setTimeout(() => {
                      window.location.reload();
                    }, 2000);
                  } catch (error) {
                    toast.error(
                      "Error cambiando el estado del estudiante, por favor intentar mas tarde"
                    );
                    console.error("Error changing status of student:", error);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <Button variant="ghost">Cambiar estado</Button>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <AlertDialog>
                  <AlertDialogTrigger>
                    <Button variant="ghost">Eliminar</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        ¿Estás absolutamente seguro?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción no se puede deshacer. Esto eliminará
                        permanentemente al estudiante y todos sus datos
                        asociados.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={async (e) => {
                          e.stopPropagation();
                          setLoading(true);
                          try {
                            await delateStudentAPI(student.ID);
                            setTimeout(() => {
                              window.location.reload();
                            }, 2000);
                          } catch (error) {
                            console.error(
                              "Error eliminando al estudiante:",
                              error
                            );
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        Sí, eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const handleExportToExcel = () => {
    const success = ExcelExporter.exportEstudiantes(filteredData);
    if (success) {
      console.log("Excel exportado exitosamente");
    } else {
      console.error("Error al exportar Excel");
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="w-full" style={{ overflowY: "scroll", padding: "30px" }}>
        <div className="bg-white rounded-lg flex justify-between items-center p-5">
          <h2 className="text-xl font-bold text-gray-900">
            Lista de estudiantes individuales
          </h2>
          <DownloadIcon
            className="h-6 w-6 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={handleExportToExcel}
            title="Exportar a Excel"
          />
        </div>

        <div className="flex items-center py-4 gap-6 justify-between">
          <Input
            placeholder="Search by name, email or ID"
            className="w-96"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <Select
            className="w-[200px]"
            onValueChange={(value) => {
              if (ciudadFilter.includes(value)) {
                setCiudadFilter(ciudadFilter.filter((city) => city !== value));
              } else {
                setCiudadFilter([...ciudadFilter, value]);
              }
            }}
          >
            <SelectTrigger className="w-[50%]">
              <SelectValue
                placeholder={ciudadFilter.length > 0
                  ? `${ciudadFilter.length} ciudades seleccionadas`
                  : "Seleccione ciudades para filtrar"}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MEDELLIN">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ciudadFilter.includes("MEDELLIN")}
                    onChange={() => {}}
                    className="h-4 w-4"
                  />
                  MEDELLÍN
                </div>
              </SelectItem>
              <SelectItem value="BOGOTA">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ciudadFilter.includes("BOGOTA")}
                    onChange={() => {}}
                    className="h-4 w-4"
                  />
                  BOGOTÁ
                </div>
              </SelectItem>
              <SelectItem value="CALI">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ciudadFilter.includes("CALI")}
                    onChange={() => {}}
                    className="h-4 w-4"
                  />
                  CALI
                </div>
              </SelectItem>
              <SelectItem value="BARRANQUILLA">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ciudadFilter.includes("BARRANQUILLA")}
                    onChange={() => {}}
                    className="h-4 w-4"
                  />
                  BARRANQUILLA
                </div>
              </SelectItem>
              <SelectItem value="BUCARAMANGA">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ciudadFilter.includes("BUCARAMANGA")}
                    onChange={() => {}}
                    className="h-4 w-4"
                  />
                  BUCARAMANGA
                </div>
              </SelectItem>
            </SelectContent>
          </Select>

          <ScrollListProfesores
            profesorSelectedToFilter={profesorSelectedToFilter}
            setprofesorSelectedToFilter={setprofesorSelectedToFilter}
            setLoading={setLoading}
            multiSelect={true}
          />

          <Button
            variant="ghost"
            onClick={() => {
              setSearchTerm("");
              setCiudadFilter([]);
              setprofesorSelectedToFilter([]);
            }}
          >
            Clear filters
          </Button>

          <Sheet>
            <SheetTrigger asChild>
              <Button>Create new student +</Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full md:w-1/3 bg-gray-100 text-black min-h-screen p-6 overflow-y-auto"
            >
              <SheetHeader>
                <SheetTitle className="text-xl font-bold mb-4">
                  Crear estudiante individual
                </SheetTitle>
                <SheetDescription>
                  Llena los datos para crear un estudiante nuevo.
                </SheetDescription>
              </SheetHeader>
              <CrearEditarEstudiante data={{}} context={"create"} />
            </SheetContent>
          </Sheet>
        </div>

        <div className="mb-4 text-sm text-gray-600">
          Mostrando {filteredData.length} estudiante(s)
          {filteredData.length !== data.length &&
            ` (filtrados de ${data.length} total)`}
        </div>

        <DataTableDemoTemplate
          columns={columns}
          dataToShow={filteredData}
          rowClickToNavigate={
            "/admin/tablaestudiantes/estudiantesprivados/studentdetail"
          }
          localstorage_name={"selected_student"}
          externalCurrentPage={currentPage}
          onPageChange={handlePageChange}
          itemsPerPage={20}
        />
      </div>
      <Toaster />
    </>
  );
}
